import { useContext, useState, useEffect, useCallback } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Theme,
  useTheme,
  useMediaQuery,
  DialogContentText,
  CircularProgress,
  FormControl,
  RadioGroup,
} from '@material-ui/core';

import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { getStaffRequest } from '@vyce/core/src/api/companies';
import { StaffListItem } from '@vyce/core/src/types';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { GridCellWithAvatar } from '@vyce/core/src/components/GridCellWithAvatar';
import { AppRoundedRadio } from '@vyce/core/src/components/inputs/AppRadio';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const TransferOwnershipDialog = ({ open, setOpen }: Props) => {
  const theme = useTheme<Theme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [staff, setStaff] = useState<StaffListItem[]>([]);
  const { selectedCompany } = useTypedSelector(state => state.helper);
  const [loading, setLoading] = useState<boolean>(false);
  const { handleServerError } = useContext(NotificationContext);
  const { user } = useTypedSelector(state => state);
  const [selectedStaff, setSelectedStaff] = useState<StaffListItem | null>(null);
  const [transferOwnershipLoading, setTransferOwnershipLoading] = useState<boolean>(false);

  const transferOwnership = async () => {
    if (!selectedStaff?.user_id || !selectedCompany?.uuid) {
      return;
    }
    try {
      setTransferOwnershipLoading(true);
    } catch (e) {
      handleServerError(e);
    } finally {
      setTransferOwnershipLoading(false);
    }
  };

  const getStaff = useCallback(async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    try {
      setLoading(true);
      const res = await getStaffRequest(selectedCompany?.uuid, {
        limit: 300,
        offset: 0,
        substring: '',
      });
      setStaff(res.data.items.filter((item: StaffListItem) => item.user_id !== user.uuid));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }, [handleServerError, selectedCompany?.uuid, user.uuid]);

  useEffect(() => {
    if (open && selectedCompany?.uuid) {
      getStaff();
    }
  }, [getStaff, selectedCompany?.uuid, open]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={fullScreen}
      open={open}
      onClose={() => setOpen(false)}>
      <DialogTitle id="responsive-dialog-title">Before you remove this company...</DialogTitle>

      <DialogContent>
        <DialogContentText>
          As you are the owner of this account you need to transfer ownership to another company
          administrator. Please choose who you’d like to transfer ownership rights to...
        </DialogContentText>

        <Box
          minHeight={250}
          maxHeight={250}
          overflow="auto"
          display="flex"
          flexDirection="column"
          gridGap={10}
          marginTop={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              {staff.map(item => (
                <Box key={item.user_id} style={{ cursor: 'pointer' }} onClick={() => setSelectedStaff(item)}>
                  <FormControl component="fieldset" style={{ width: '100%' }}>
                    <RadioGroup value={item.user_id || null} style={{ width: '100%' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={8}>
                        <GridCellWithAvatar
                          avatarUrl={item.photo?.url}
                          name={`${item.first_name} ${item.last_name}`}
                        />

                        <AppRoundedRadio
                          color="primary"
                          name={item.user_id}
                          checked={selectedStaff?.user_id === item.user_id}
                        />
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </Box>
              ))}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ flexWrap: 'wrap', gridGap: 10 }}>
        <Button size="large" onClick={() => setOpen(false)} variant="outlined">
          Cancel
        </Button>

        <Button
          size="large"
          onClick={transferOwnership}
          variant="outlined"
          style={{ width: 400 }}
          color="primary"
          disabled={transferOwnershipLoading || !selectedStaff?.user_id}>
          <ButtonTitleWithLoading
            title="Transfer & delete this company from my profile"
            loaderVariant="primary"
            loading={transferOwnershipLoading}
          />
        </Button>

        <Button
          size="large"
          onClick={transferOwnership}
          variant="outlined"
          style={{ width: 350 }}
          color="primary"
          disabled={transferOwnershipLoading || !selectedStaff?.user_id}>
          <ButtonTitleWithLoading
            title="Transfer & keep me as a Company Admin"
            loaderVariant="primary"
            loading={transferOwnershipLoading}
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
